/**
 * CostRatesPanel.jsx - Engineers > Staff cost rates: the GAT-B workbook's
 * Staff table (0. INFO) in GlobalOps. One row per person: location,
 * employment type, skill level and type, OT paid?, hourly pay, burden
 * multiplier, and the burden rate the quotes and projects are costed at
 * (hourly x multiplier + hourly, worked out by the server).
 *
 * Only for the 'cost-rates' area ("Staff cost rates & COGS"): View reads,
 * Edit changes. Each row saves on its own. A row can be linked to the
 * Engineers roster so the ADP timesheets are costed at that person's rate;
 * names that match without doubt are offered as suggestions, the rest are
 * listed for linking by hand.
 */
import { useEffect, useMemo, useState } from 'react';
import { Btn, GREEN, LINE, MUTED, Section, inputStyle } from '../quotes/quoteUi';
import { addStaff, deleteStaff, getStaffCosts, importStaffFile, linkStaff, money, saveStaff } from '../costs/costApi';

const LEVELS = ['Level I', 'Level II', 'Level III'];
const EMPL = ['W-2', '1099', 'Contractor'];
const cell = { ...inputStyle, padding: '4px 6px', fontSize: 11.5 };
const blankRow = () => ({
  name: '',
  location: '',
  emplType: 'W-2',
  skillLevel: 'Level II',
  skillType: '',
  otPaid: true,
  hourlyRate: '',
  burdenMult: '0.4',
  active: true,
  engineerId: null,
  _dirty: true,
});

export default function CostRatesPanel({ editable }) {
  const [state, setState] = useState({ phase: 'loading' });
  const [rows, setRows] = useState([]);
  const [msg, setMsg] = useState(null);
  const [filter, setFilter] = useState('');

  const load = async () => {
    const r = await getStaffCosts();
    if (!r.ok || !r.body) {
      setState({ phase: 'failed', error: (r.body && r.body.error) || 'Could not load the staff cost rates.' });
      return;
    }
    setRows(r.body.staff);
    setState({ phase: 'ready', ...r.body });
  };
  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const engineers = useMemo(() => (state.engineers || []).filter((e) => !e.archived), [state.engineers]);
  const engName = (id) => ((state.engineers || []).find((e) => e.id === id) || {}).name || '';
  if (state.phase === 'loading') return <div style={{ padding: 30, color: MUTED, fontSize: 12 }}>Loading the staff cost rates...</div>;
  if (state.phase === 'failed')
    return (
      <div role="alert" style={{ padding: 30, color: '#B71C1C', fontSize: 12, fontWeight: 600 }}>
        {state.error}
      </div>
    );

  const set = (i, k, v) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, [k]: v, _dirty: true } : r)));
  const save = async (i) => {
    const r = rows[i];
    const res = r.id ? await saveStaff(r.id, r) : await addStaff(r);
    if (!res.ok) {
      setMsg({ kind: 'error', text: (res.body && res.body.error) || 'Could not save the row.' });
      return;
    }
    setMsg({ kind: 'ok', text: `${res.body.name} saved: burden rate ${money(res.body.burdenRate)}/hr.` });
    setRows((rs) => rs.map((x, j) => (j === i ? res.body : x)));
  };
  const importFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    const res = await importStaffFile(file);
    if (!res.ok) {
      setMsg({ kind: 'error', text: (res.body && res.body.error) || 'Could not import that file.' });
      return;
    }
    const b = res.body;
    setMsg({
      kind: 'ok',
      text: `Imported: ${b.created} added, ${b.updated} updated, ${b.unchanged} unchanged, ${b.linked} linked to engineers${b.refused.length ? `; refused ${b.refused.map((x) => x.name).join(', ')}` : ''}.`,
    });
    load();
  };
  const remove = async (i) => {
    const r = rows[i];
    if (!r.id) {
      setRows((rs) => rs.filter((_, j) => j !== i));
      return;
    }
    if (
      !window.confirm(
        `Remove ${r.name} from the staff cost table? Quotes and projects that name them are then costed at their level's average.`
      )
    )
      return;
    const res = await deleteStaff(r.id);
    if (!res.ok) {
      setMsg({ kind: 'error', text: (res.body && res.body.error) || 'Could not remove the row.' });
      return;
    }
    setRows((rs) => rs.filter((_, j) => j !== i));
  };
  const link = async (links) => {
    const res = await linkStaff(links);
    if (!res.ok) {
      setMsg({ kind: 'error', text: (res.body && res.body.error) || 'Could not link.' });
      return;
    }
    setMsg({ kind: 'ok', text: `${res.body.linked} linked.` });
    load();
  };

  const q = filter.trim().toLowerCase();
  const shown = rows
    .map((r, i) => [r, i])
    .filter(([r]) => !q || `${r.name} ${r.location} ${r.skillType} ${engName(r.engineerId)}`.toLowerCase().includes(q));
  const inp = (i, k, w, num) => (
    <input
      aria-label={`${k} ${rows[i].name || i + 1}`}
      value={rows[i][k] ?? ''}
      readOnly={!editable}
      inputMode={num ? 'decimal' : undefined}
      onChange={(e) => set(i, k, num ? e.target.value.replace(/[^0-9.]/g, '') : e.target.value)}
      style={{ ...cell, width: w, textAlign: num ? 'right' : 'left', ...(editable ? null : { background: '#F7F8F5' }) }}
    />
  );
  const sel = (i, k, options, w) => (
    <select
      aria-label={`${k} ${rows[i].name || i + 1}`}
      value={rows[i][k] ?? ''}
      disabled={!editable}
      onChange={(e) => set(i, k, e.target.value)}
      style={{ ...cell, width: w }}
    >
      {!options.includes(rows[i][k]) && rows[i][k] ? <option value={rows[i][k]}>{rows[i][k]}</option> : null}
      {options.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
  );
  const avg = state.levelAverages || {};

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: GREEN }}>Staff cost rates</div>
        <div style={{ fontSize: 11, color: MUTED, maxWidth: 900 }}>
          What each person costs the company per hour: the burden rate is the hourly pay x the burden multiplier + the hourly pay. Quotes,
          project budgets and the time on a project are costed with it; the customer is billed from the Quote Rates. Only people with Staff
          cost rates &amp; COGS access see this page or any cost it produces.
          {editable ? '' : ' You can view these; changing them needs Edit.'}
        </div>
      </div>
      {msg && (
        <div
          role={msg.kind === 'error' ? 'alert' : 'status'}
          style={{
            marginBottom: 10,
            padding: '8px 12px',
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 600,
            background: msg.kind === 'error' ? '#FFEBEE' : '#E8F5E9',
            color: msg.kind === 'error' ? '#B71C1C' : GREEN,
          }}
        >
          {msg.text}
        </div>
      )}

      <Section
        title="Level averages"
        hint="A quote line for a level with nobody named is costed at the average burden rate of the active people at that level."
      >
        <div style={{ display: 'flex', gap: 18, fontSize: 12 }}>
          {LEVELS.map((L) => (
            <span key={L}>
              <b>{L}:</b> {avg[L] == null ? 'nobody' : `${money(avg[L])}/hr`}
            </span>
          ))}
        </div>
      </Section>

      {editable && (state.suggestions || []).length > 0 && (
        <Section
          title="Link to the Engineers roster"
          hint="These names match one engineer each. Linking costs that engineer's ADP timesheet hours at their rate."
        >
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
            {state.suggestions.map((s) => (
              <span key={s.staffId} style={{ fontSize: 11.5, padding: '3px 8px', background: '#F1F8E9', borderRadius: 4 }}>
                {s.name} = {s.engineerName}
              </span>
            ))}
          </div>
          <Btn small primary onClick={() => link(state.suggestions.map((s) => ({ staffId: s.staffId, engineerId: s.engineerId })))}>
            Link all {state.suggestions.length}
          </Btn>
        </Section>
      )}
      {(state.unmatched || []).length > 0 && (
        <div
          style={{
            fontSize: 11,
            color: '#92400E',
            background: '#FFF8E1',
            border: '1px solid #FDE68A',
            borderRadius: 6,
            padding: '6px 10px',
            marginBottom: 12,
          }}
        >
          Not linked to an engineer: {state.unmatched.map((u) => u.name).join(', ')}. Pick their engineer in the Engineer column
          (contractors often have none).
        </div>
      )}

      <Section
        title="Staff"
        right={
          editable ? (
            <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <label
                style={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  cursor: 'pointer',
                  padding: '4px 10px',
                  border: '1px solid #D6DCCB',
                  borderRadius: 6,
                  background: '#fff',
                }}
                title="A workbook with the Staff table (Names, Location, Empl Type, Skill Level, Skill Type, OT Paid?, Hrly Rate, Burden Multiplier) - e.g. the quote workbook. Names already here are updated, new ones added."
              >
                Import from Excel
                <input
                  type="file"
                  aria-label="Import staff from Excel"
                  accept=".xlsx,.xlsm,.xls"
                  style={{ display: 'none' }}
                  onChange={importFile}
                />
              </label>
              <Btn small onClick={() => setRows((rs) => [...rs, blankRow()])}>
                + Add person
              </Btn>
            </span>
          ) : null
        }
      >
        <input
          aria-label="Search the staff"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Search name, location, type, engineer"
          style={{ ...inputStyle, maxWidth: 320, marginBottom: 8 }}
        />
        <div style={{ overflowX: 'auto' }}>
          <table aria-label="Staff cost rates" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {[
                  'Name',
                  'Engineer',
                  'Location',
                  'Empl type',
                  'Skill level',
                  'Skill type',
                  'OT paid?',
                  'Hourly ($)',
                  'Burden mult',
                  'Burden rate',
                  'Active',
                  '',
                ].map((h) => (
                  <th
                    key={h}
                    style={{
                      textAlign: 'left',
                      fontSize: 10,
                      fontWeight: 700,
                      color: MUTED,
                      textTransform: 'uppercase',
                      padding: '4px 6px',
                      borderBottom: `1px solid ${LINE}`,
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shown.map(([r, i]) => (
                <tr key={r.id || `new-${i}`} style={{ opacity: r.active === false ? 0.55 : 1 }}>
                  <td style={{ padding: 2 }}>{inp(i, 'name', 130)}</td>
                  <td style={{ padding: 2 }}>
                    <select
                      aria-label={`Engineer for ${r.name || i + 1}`}
                      value={r.engineerId || ''}
                      disabled={!editable}
                      onChange={(e) => set(i, 'engineerId', e.target.value || null)}
                      style={{ ...cell, width: 150 }}
                    >
                      <option value="">- none -</option>
                      {r.engineerId && !engineers.some((e) => e.id === r.engineerId) && (
                        <option value={r.engineerId}>{engName(r.engineerId) || r.engineerId} (archived)</option>
                      )}
                      {engineers.map((e) => (
                        <option key={e.id} value={e.id}>
                          {e.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td style={{ padding: 2 }}>{inp(i, 'location', 100)}</td>
                  <td style={{ padding: 2 }}>{sel(i, 'emplType', EMPL, 96)}</td>
                  <td style={{ padding: 2 }}>{sel(i, 'skillLevel', LEVELS, 90)}</td>
                  <td style={{ padding: 2 }}>{inp(i, 'skillType', 110)}</td>
                  <td style={{ padding: 2, textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      aria-label={`OT paid ${r.name || i + 1}`}
                      checked={r.otPaid !== false}
                      disabled={!editable}
                      onChange={(e) => set(i, 'otPaid', e.target.checked)}
                    />
                  </td>
                  <td style={{ padding: 2 }}>{inp(i, 'hourlyRate', 80, true)}</td>
                  <td style={{ padding: 2 }}>{inp(i, 'burdenMult', 70, true)}</td>
                  <td style={{ padding: '2px 6px', fontSize: 11.5, fontWeight: 700, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {r._dirty ? (
                      <span style={{ color: MUTED }}>
                        {money((Number(r.hourlyRate) || 0) * (Number(r.burdenMult) || 0) + (Number(r.hourlyRate) || 0))}
                      </span>
                    ) : (
                      money(r.burdenRate)
                    )}
                  </td>
                  <td style={{ padding: 2, textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      aria-label={`Active ${r.name || i + 1}`}
                      checked={r.active !== false}
                      disabled={!editable}
                      onChange={(e) => set(i, 'active', e.target.checked)}
                    />
                  </td>
                  <td style={{ padding: 2, whiteSpace: 'nowrap' }}>
                    {editable && r._dirty && (
                      <Btn small primary onClick={() => save(i)}>
                        Save
                      </Btn>
                    )}{' '}
                    {editable && (
                      <Btn small danger aria-label={`Remove ${r.name || 'new person'}`} onClick={() => remove(i)}>
                        x
                      </Btn>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ marginTop: 6, fontSize: 10.5, color: MUTED }}>
          {rows.length} people. OT paid? unticked = always costed at straight time (the workbook's contractors).
        </div>
      </Section>
    </div>
  );
}
